import {
  Navigate,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";

import { LogoutLoader } from "./auth/logout";
import { protectedLoader } from "./portal/layout";
import PermissionRoute from "~/components/shared/permission-route";
import ModuleRoute from "~/components/shared/module-route";
import { PermissionKeys } from "~/lib/constants";
import { lazy } from "react";

const ForgotPasswordPage = lazy(() => import("./auth/forgot-password"));
const AuthLayout = lazy(() => import("./auth/layout"));
const LoginPage = lazy(() => import("./auth/login"));
const Logout = lazy(() => import("./auth/logout"));
const ResetPasswordPage = lazy(() => import("./auth/reset-password"));
const SetPasswordPage = lazy(() => import("./auth/set-password"));
const RootBoundary = lazy(() => import("./error"));
const Layout = lazy(() => import("./layout"));
const ItemLayout = lazy(() => import("./portal/itemManagement/layout"));
const PortalLayout = lazy(() => import("./portal/layout"));
const Dashboard = lazy(() => import("./portal"));
const FindingsPage = lazy(() => import("./portal/findings"));
const ActionsPage = lazy(() => import("./portal/actions"));
const SitePage = lazy(() => import("./portal/sites"));
const SiteDetails = lazy(() => import("./portal/sites/details"));
const AuditTrail = lazy(() => import("./portal/auditTrail"));
const IncidentsPage = lazy(() => import("./portal/itemManagement/incidents"));
const IncidentsCreatePage = lazy(
  () => import("./portal/itemManagement/incidents/create"),
);
const IncidentsEditPage = lazy(
  () => import("./portal/itemManagement/incidents/edit"),
);
const IncidentsDetail = lazy(
  () => import("./portal/itemManagement/incidents/details"),
);
const ObservationsPage = lazy(
  () => import("./portal/itemManagement/observations"),
);
const ObservationsCreatePage = lazy(
  () => import("./portal/itemManagement/observations/create"),
);
const ObservationsEditPage = lazy(
  () => import("./portal/itemManagement/observations/edit"),
);
const ObservationsDetail = lazy(
  () => import("./portal/itemManagement/observations/details"),
);
const AuditsPage = lazy(() => import("./portal/itemManagement/audits"));
const AuditsDetail = lazy(
  () => import("./portal/itemManagement/audits/details"),
);
const AuditsCreatePage = lazy(
  () => import("./portal/itemManagement/audits/create"),
);
const AuditsCompletePage = lazy(
  () => import("./portal/itemManagement/audits/complete"),
);
const InspectionsPage = lazy(
  () => import("./portal/itemManagement/inspections"),
);
const InspectionsDetail = lazy(
  () => import("./portal/itemManagement/inspections/details"),
);
const InspectionsCreatePage = lazy(
  () => import("./portal/itemManagement/inspections/create"),
);
const InspectionsCompletePage = lazy(
  () => import("./portal/itemManagement/inspections/complete"),
);
const FindingsIndex = lazy(() => import("./portal/itemManagement/findings"));
const FindingsDetail = lazy(
  () => import("./portal/itemManagement/findings/details"),
);
const SettingsPage = lazy(() => import("../components/pages/settings"));
const FormBuilderPage = lazy(
  () => import("../components/pages/settings/form-builder"),
);
const CreateTemplatePage = lazy(
  () => import("../components/pages/settings/form-builder/templates/create"),
);
const EditTemplatePage = lazy(
  () => import("../components/pages/settings/form-builder/templates/edit"),
);
const TagsPage = lazy(() => import("../components/pages/settings/tags"));
const FormOptionsPage = lazy(
  () => import("../components/pages/settings/form-options/tabs"),
);
const CurrencySettingsPage = lazy(
  () => import("../components/pages/settings/currency-settings"),
);
const ChangePasswordPage = lazy(
  () => import("../components/pages/settings/chg-pwd"),
);
const ApprovalsPage = lazy(
  () => import("../components/pages/settings/approvals"),
);
const AlertsPage = lazy(() => import("../components/pages/settings/alerts"));
const RiskMatrixPage = lazy(
  () => import("../components/pages/settings/risk-matrix"),
);
const InternalTrainingsPage = lazy(
  () => import("./portal/complianceManagement/personnel/trainings"),
);
const InternalTrainingDetailsPage = lazy(
  () => import("./portal/complianceManagement/personnel/trainings/details"),
);
const EligibilityPermitsPage = lazy(
  () => import("./portal/complianceManagement/personnel/eligibility-permits"),
);
const CreateEligibilityPermitPage = lazy(
  () =>
    import("./portal/complianceManagement/personnel/eligibility-permits/create"),
);
const EligibilityPermitDetailsPage = lazy(
  () =>
    import("./portal/complianceManagement/personnel/eligibility-permits/details"),
);
const EditEligibilityPermitPage = lazy(
  () =>
    import("./portal/complianceManagement/personnel/eligibility-permits/edit"),
);

const CompetencePermitsPage = lazy(
  () => import("./portal/complianceManagement/personnel/competence-permits"),
);
const CreateCompetencePermitPage = lazy(
  () =>
    import("./portal/complianceManagement/personnel/competence-permits/create"),
);
const CompetencePermitDetailsPage = lazy(
  () =>
    import("./portal/complianceManagement/personnel/competence-permits/details"),
);
const EditCompetencePermitPage = lazy(
  () =>
    import("./portal/complianceManagement/personnel/competence-permits/edit"),
);

const CompanyWidePermitsPage = lazy(
  () =>
    import("./portal/complianceManagement/organizational/company-wide-permits"),
);
const CreateCompanyWidePermitPage = lazy(
  () =>
    import("./portal/complianceManagement/organizational/company-wide-permits/create"),
);
const CompanyWidePermitDetailsPage = lazy(
  () =>
    import("./portal/complianceManagement/organizational/company-wide-permits/details"),
);
const EditCompanyWidePermitPage = lazy(
  () =>
    import("./portal/complianceManagement/organizational/company-wide-permits/create"),
);

const SiteSpecificPermitsPage = lazy(
  () =>
    import("./portal/complianceManagement/organizational/site-specific-permits"),
);
const CreateSiteSpecificPermitPage = lazy(
  () =>
    import("./portal/complianceManagement/organizational/site-specific-permits/create"),
);
const SiteSpecificPermitDetailsPage = lazy(
  () =>
    import("./portal/complianceManagement/organizational/site-specific-permits/details"),
);
const EditSiteSpecificPermitPage = lazy(
  () =>
    import("./portal/complianceManagement/organizational/site-specific-permits/create"),
);
const EnvironmentalRecordsPage = lazy(
  () => import("../components/pages/recordManagement/environmental"),
);
const HealthRecordsPage = lazy(
  () => import("../components/pages/recordManagement/health"),
);
const MeetingRecordsPage = lazy(
  () => import("../components/pages/recordManagement/meeting"),
);
const CreateMeetingPage = lazy(
  () => import("../components/pages/recordManagement/meeting/create"),
);
const UpdateMeetingPage = lazy(
  () => import("../components/pages/recordManagement/meeting/update-meeting"),
);
const ViewMeetingPage = lazy(
  () => import("../components/pages/recordManagement/meeting/details"),
);
const ManHoursPage = lazy(
  () => import("../components/pages/recordManagement/work-hours-register"),
);
const CreateManHoursPage = lazy(
  () =>
    import("../components/pages/recordManagement/work-hours-register/create"),
);
const WorkHoursDetailsPage = lazy(
  () =>
    import("../components/pages/recordManagement/work-hours-register/details"),
);
const EmployeesPage = lazy(
  () => import("../components/pages/resourceManagement/employees"),
);
const CreateEmployeePage = lazy(
  () => import("../components/pages/resourceManagement/employees/create"),
);
const EditEmployeePage = lazy(
  () => import("../components/pages/resourceManagement/employees/edit"),
);
const ViewEmployeePage = lazy(
  () => import("../components/pages/resourceManagement/employees/details"),
);
const AccessManagePage = lazy(
  () => import("../components/pages/resourceManagement/access"),
);
const ManageRolesPage = lazy(
  () => import("../components/pages/resourceManagement/access/manage-roles"),
);
const CreateRolePage = lazy(
  () =>
    import("../components/pages/resourceManagement/access/manage-roles/create"),
);
const EditRolePage = lazy(
  () =>
    import("../components/pages/resourceManagement/access/manage-roles/edit"),
);
const DocumentsPage = lazy(
  () => import("../components/pages/resourceManagement/documents"),
);
const InventoryPage = lazy(
  () => import("./portal/record-management/inventory"),
);
const RiskRegisterPage = lazy(
  () => import("../components/pages/resourceManagement/registers/risk"),
);
const CreateRiskRegisterPage = lazy(
  () => import("../components/pages/resourceManagement/registers/risk/create"),
);
const ChemicalRegisterPage = lazy(
  () => import("../components/pages/resourceManagement/registers/chemical"),
);
const CreateChemicalRegisterPage = lazy(
  () =>
    import("../components/pages/resourceManagement/registers/chemical/create"),
);
const ControlLibraryPage = lazy(
  () => import("../components/pages/resourceManagement/registers/controls"),
);
const CreateControlLibraryPage = lazy(
  () =>
    import("../components/pages/resourceManagement/registers/controls/create"),
);
const MarketplacePage = lazy(() => import("../components/pages/marketplace"));
const AssetsPage = lazy(() => import("./portal/assetManagement/assets"));
const AssetsCreatePage = lazy(
  () => import("./portal/assetManagement/assets/create"),
);
const AssetsEditPage = lazy(
  () => import("./portal/assetManagement/assets/edit"),
);
const AssetsDetail = lazy(
  () => import("./portal/assetManagement/assets/details"),
);
const ReportsNewPage = lazy(() => import("../components/pages/reports"));
import ReportsNewLayout from "../components/pages/reports/Layout";
import ReportsNewQueryListPage from "../components/pages/reports/query";
import ReportsNewQueryCreatePage from "../components/pages/reports/query/create";
import ReportsNewQueryEditPage from "../components/pages/reports/query/edit";
import ReportsNewDashboard from "../components/pages/reports/DashboardPage";

import JSAPage from "../components/pages/dailyOps/jsa";
import CreateJSAPage from "../components/pages/dailyOps/jsa/form";
import COSHHPage from "../components/pages/dailyOps/coshh";
import CreateCOSHHPage from "../components/pages/dailyOps/coshh/create";
import { DynamicRiskAssessmentList } from "../components/pages/dailyOps/dra/DynamicRiskAssessmentList";
import { DynamicRiskAssessmentCreate } from "../components/pages/dailyOps/dra/DynamicRiskAssessmentCreate";

import PTWPage from "../components/pages/dailyOps/ptw";
import CreatePTWPage from "../components/pages/dailyOps/ptw/create";
import LiftingOperationsPage from "../components/pages/dailyOps/lifting-operations";

// Daily Ops

const rootRoutes = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<Layout />} ErrorBoundary={RootBoundary}>
      <Route
        path="/"
        element={<PortalLayout />}
        loader={protectedLoader}
        ErrorBoundary={RootBoundary}
      >
        {/* ── Root redirect: / → /hse/home ─────────────────────────── */}
        <Route index element={<Navigate to="/hse/home" replace />} />

        {/* ── Redirect shims: old paths → new /hse/ paths ──────────── */}
        {/* Keep these so any existing bookmarks, email links etc. still work */}
        <Route path="home" element={<Navigate to="/hse/home" replace />} />
        <Route
          path="reports"
          element={<Navigate to="/hse/reports" replace />}
        />
        <Route
          path="reports/*"
          element={<Navigate to="/hse/reports" replace />}
        />
        <Route path="sites" element={<Navigate to="/hse/sites" replace />} />
        <Route
          path="sites/:id"
          element={<Navigate to="/hse/sites" replace />}
        />
        <Route
          path="incidents"
          element={<Navigate to="/hse/incidents" replace />}
        />
        <Route
          path="incidents/*"
          element={<Navigate to="/hse/incidents" replace />}
        />
        <Route
          path="observations"
          element={<Navigate to="/hse/observations" replace />}
        />
        <Route
          path="observations/*"
          element={<Navigate to="/hse/observations" replace />}
        />
        <Route path="audits" element={<Navigate to="/hse/audits" replace />} />
        <Route
          path="audits/*"
          element={<Navigate to="/hse/audits" replace />}
        />
        <Route
          path="inspections"
          element={<Navigate to="/hse/inspections" replace />}
        />
        <Route
          path="inspections/*"
          element={<Navigate to="/hse/inspections" replace />}
        />
        <Route
          path="findings"
          element={<Navigate to="/hse/findings" replace />}
        />
        <Route
          path="findings/*"
          element={<Navigate to="/hse/findings" replace />}
        />
        <Route
          path="actions"
          element={<Navigate to="/hse/actions" replace />}
        />
        <Route
          path="actions/*"
          element={<Navigate to="/hse/actions" replace />}
        />
        <Route
          path="compliance/*"
          element={<Navigate to="/hse/compliance" replace />}
        />
        <Route
          path="records/*"
          element={<Navigate to="/hse/records/environmental" replace />}
        />
        <Route
          path=":type/:id/findings"
          element={<Navigate to="/hse/findings" replace />}
        />

        {/* ── HSE Module ──────────────────────────────────────────────── */}
        <Route element={<ModuleRoute moduleKey="HSE" />}>
          {/* Dashboard / Home */}
          <Route path="hse/home" element={<Dashboard />} />

          {/* Reports */}
          <Route path="hse/reports" element={<ReportsNewLayout />}>
            <Route index element={<ReportsNewPage />} />
            <Route path="dashboard" element={<ReportsNewDashboard />} />
            <Route path="query" element={<ItemLayout />}>
              <Route index element={<ReportsNewQueryListPage />} />
              <Route path="create" element={<ReportsNewQueryCreatePage />} />
              <Route path="edit/:id" element={<ReportsNewQueryEditPage />} />
            </Route>
          </Route>

          {/* Sites */}
          <Route path="hse/sites" element={<ItemLayout />}>
            <Route index element={<SitePage />} />
            <Route path=":id" element={<SiteDetails />} />
          </Route>

          {/* Findings by item type (e.g. /hse/inspections/123/findings) */}
          <Route path="hse/:type/:id/findings" element={<FindingsPage />} />

          {/* Actions */}
          <Route path="hse/actions" element={<ItemLayout />}>
            <Route index element={<ActionsPage />} />
          </Route>

          {/* Audit Trail */}
          <Route path="hse/audit-trail" element={<AuditTrail />} />

          {/* Incidents */}
          <Route
            element={
              <PermissionRoute
                permissionKey={PermissionKeys.itemManagement.incidents.view}
              />
            }
          >
            <Route path="hse/incidents" element={<ItemLayout />}>
              <Route index element={<IncidentsPage />} />
              <Route path="create" element={<IncidentsCreatePage />} />
              <Route path="edit/:id" element={<IncidentsEditPage />} />
              <Route path="view/:id" element={<IncidentsDetail />} />
            </Route>
          </Route>

          {/* Observations */}
          <Route
            element={
              <PermissionRoute
                permissionKey={PermissionKeys.itemManagement.observations.view}
              />
            }
          >
            <Route path="hse/observations" element={<ItemLayout />}>
              <Route index element={<ObservationsPage />} />
              <Route path="create" element={<ObservationsCreatePage />} />
              <Route path="edit/:id" element={<ObservationsEditPage />} />
              <Route path="view/:id" element={<ObservationsDetail />} />
            </Route>
          </Route>

          {/* Audits */}
          <Route
            element={
              <PermissionRoute
                permissionKey={PermissionKeys.itemManagement.audits.view}
              />
            }
          >
            <Route path="hse/audits" element={<ItemLayout />}>
              <Route index element={<AuditsPage />} />
              <Route path="view/:id" element={<AuditsDetail />} />
              <Route path="create" element={<AuditsCreatePage />} />
              <Route path=":id/complete" element={<AuditsCompletePage />} />
            </Route>
          </Route>

          {/* Inspections */}
          <Route
            element={
              <PermissionRoute
                permissionKey={PermissionKeys.itemManagement.inspections.view}
              />
            }
          >
            <Route path="hse/inspections" element={<ItemLayout />}>
              <Route index element={<InspectionsPage />} />
              <Route path="view/:id" element={<InspectionsDetail />} />
              <Route path="create" element={<InspectionsCreatePage />} />
              <Route
                path=":id/complete"
                element={<InspectionsCompletePage />}
              />
            </Route>
          </Route>

          {/* Findings (standalone list) */}
          <Route path="hse/findings" element={<ItemLayout />}>
            <Route index element={<FindingsIndex />} />
            <Route path=":id/detail" element={<FindingsDetail />} />
          </Route>

          {/* Compliance — Personnel Permits */}
          <Route
            element={
              <PermissionRoute
                permissionKey={
                  PermissionKeys.complianceManagement.eligibilityPermits.view
                }
              />
            }
          >
            <Route
              path="hse/compliance/personnel/eligibility-permits"
              element={<ItemLayout />}
            >
              <Route index element={<EligibilityPermitsPage />} />
              <Route path="create" element={<CreateEligibilityPermitPage />} />
              <Route
                path=":id/detail"
                element={<EligibilityPermitDetailsPage />}
              />
              <Route path="edit/:id" element={<EditEligibilityPermitPage />} />
            </Route>
          </Route>

          <Route
            element={
              <PermissionRoute
                permissionKey={
                  PermissionKeys.complianceManagement.competencePermits.view
                }
              />
            }
          >
            <Route
              path="hse/compliance/personnel/competence-permits"
              element={<ItemLayout />}
            >
              <Route index element={<CompetencePermitsPage />} />
              <Route path="create" element={<CreateCompetencePermitPage />} />
              <Route
                path=":id/detail"
                element={<CompetencePermitDetailsPage />}
              />
              <Route path="edit/:id" element={<EditCompetencePermitPage />} />
            </Route>
          </Route>

          {/* Compliance — Org Permits */}
          <Route
            element={
              <PermissionRoute
                permissionKey={
                  PermissionKeys.complianceManagement.companyWidePermits.view
                }
              />
            }
          >
            <Route
              path="hse/compliance/organizational/company-wide-permits"
              element={<ItemLayout />}
            >
              <Route index element={<CompanyWidePermitsPage />} />
              <Route path="create" element={<CreateCompanyWidePermitPage />} />
              <Route
                path=":id/detail"
                element={<CompanyWidePermitDetailsPage />}
              />
              <Route path="edit/:id" element={<EditCompanyWidePermitPage />} />
            </Route>
          </Route>

          <Route
            element={
              <PermissionRoute
                permissionKey={
                  PermissionKeys.complianceManagement.siteSpecificPermits.view
                }
              />
            }
          >
            <Route
              path="hse/compliance/organizational/site-specific-permits"
              element={<ItemLayout />}
            >
              <Route index element={<SiteSpecificPermitsPage />} />
              <Route path="create" element={<CreateSiteSpecificPermitPage />} />
              <Route
                path=":id/detail"
                element={<SiteSpecificPermitDetailsPage />}
              />
              <Route path="edit/:id" element={<EditSiteSpecificPermitPage />} />
            </Route>
          </Route>

          {/* Records */}
          <Route
            path="hse/records/environmental"
            element={<EnvironmentalRecordsPage />}
          />
          <Route path="hse/records/health" element={<HealthRecordsPage />} />

          <Route
            element={
              <PermissionRoute
                permissionKey={
                  PermissionKeys.recordManagement.meetingRecords.view
                }
              />
            }
          >
            <Route
              path="hse/records/meeting"
              element={<MeetingRecordsPage />}
            />
            <Route
              path="hse/records/meeting/create"
              element={<CreateMeetingPage />}
            />
            <Route
              path="hse/records/meeting/edit/:id"
              element={<UpdateMeetingPage />}
            />
            <Route
              path="hse/records/meeting/:id"
              element={<ViewMeetingPage />}
            />
          </Route>

          <Route
            element={
              <PermissionRoute
                permissionKey={
                  PermissionKeys.recordManagement.manHourRecords.view
                }
              />
            }
          >
            <Route path="hse/records/work-hours" element={<ManHoursPage />} />
            <Route
              path="hse/records/work-hours/create"
              element={<CreateManHoursPage />}
            />
            <Route
              path="hse/records/work-hours/:id"
              element={<WorkHoursDetailsPage />}
            />
          </Route>

          {/* Trainings (compliance/personnel) */}
          <Route
            path="hse/compliance/personnel/trainings"
            element={<ItemLayout />}
          >
            <Route index element={<InternalTrainingsPage />} />
            <Route path=":id" element={<InternalTrainingDetailsPage />} />
          </Route>

          {/* Daily Ops Module */}
          <Route path="daily-ops">
            <Route index element={<Navigate to="jsa" replace />} />
            <Route
              element={
                <PermissionRoute
                  permissionKey={PermissionKeys.dailyOperations.jsa.view}
                />
              }
            >
              <Route path="jsa" element={<ItemLayout />}>
                <Route index element={<JSAPage />} />
                <Route path="create" element={<CreateJSAPage />} />
              </Route>
            </Route>
            <Route
              element={
                <PermissionRoute
                  permissionKey={PermissionKeys.dailyOperations.coshh.view}
                />
              }
            >
              <Route path="coshh" element={<ItemLayout />}>
                <Route index element={<COSHHPage />} />
                <Route path="create" element={<CreateCOSHHPage />} />
              </Route>
            </Route>
            <Route
              element={
                <PermissionRoute
                  permissionKey={PermissionKeys.dailyOperations.dra.view}
                />
              }
            >
              <Route path="dra" element={<ItemLayout />}>
                <Route index element={<DynamicRiskAssessmentList />} />
                <Route path="new" element={<DynamicRiskAssessmentCreate />} />
              </Route>
            </Route>
            <Route
              element={
                <PermissionRoute
                  permissionKey={PermissionKeys.dailyOperations.ptw.view}
                />
              }
            >
              <Route path="ptw" element={<ItemLayout />}>
                <Route index element={<PTWPage />} />
                <Route path="create" element={<CreatePTWPage />} />
              </Route>
            </Route>
            <Route
              element={
                <PermissionRoute
                  permissionKey={
                    PermissionKeys.dailyOperations.liftingOperations.view
                  }
                />
              }
            >
              <Route path="lifting-operations" element={<ItemLayout />}>
                <Route index element={<LiftingOperationsPage />} />
              </Route>
            </Route>
          </Route>
        </Route>
        {/* ── End HSE Module ──────────────────────────────────────────── */}

        {/* ── Module Stubs (guarded by module access) ───────────────── */}
        <Route element={<ModuleRoute moduleKey="Finance" />}>
          <Route path="finance" element={<></>} />
        </Route>

        <Route element={<ModuleRoute moduleKey="Assets" />}>
          <Route path="asset/assets" element={<ItemLayout />}>
            <Route index element={<AssetsPage />} />
            <Route path="create" element={<AssetsCreatePage />} />
            <Route path="edit/:id" element={<AssetsEditPage />} />
            <Route path="view/:id" element={<AssetsDetail />} />
          </Route>
        </Route>

        <Route element={<ModuleRoute moduleKey="Contractors" />}>
          <Route path="contractors" element={<></>} />
        </Route>

        <Route element={<ModuleRoute moduleKey="Resources" />}>
          <Route path="resources" element={<></>} />
          <Route path="resource/inventory/:type" element={<InventoryPage />} />
          <Route
            path="resource/registers/risk"
            element={<RiskRegisterPage />}
          />
          <Route
            path="resource/registers/risk/create"
            element={<CreateRiskRegisterPage />}
          />
          <Route
            path="resource/registers/chemical"
            element={<ChemicalRegisterPage />}
          />
          <Route
            path="resource/registers/chemical/create"
            element={<CreateChemicalRegisterPage />}
          />
          <Route
            path="resource/registers/controls"
            element={<ControlLibraryPage />}
          />
          <Route
            path="resource/registers/controls/create"
            element={<CreateControlLibraryPage />}
          />
        </Route>

        <Route element={<ModuleRoute moduleKey="Personnel" />}>
          <Route path="personnel" element={<></>} />
          <Route path="resource/employees" element={<ItemLayout />}>
            <Route index element={<EmployeesPage />} />
            <Route path="create" element={<CreateEmployeePage />} />
            <Route path="edit/:id" element={<EditEmployeePage />} />
            <Route path="view/:id" element={<ViewEmployeePage />} />
          </Route>
        </Route>

        {/* ── Unchanged / Cross-Module Routes ───────────────────────── */}
        <Route path="marketplace" element={<MarketplacePage />} />
        <Route path="audit-trail" element={<AuditTrail />} />

        <Route path="settings" element={<SettingsPage />}>
          <Route index element={<FormBuilderPage />} />
          <Route path="create-template" element={<CreateTemplatePage />} />
          <Route path="template/:id/edit" element={<EditTemplatePage />} />
          <Route path="tags" element={<TagsPage />} />
          <Route path="form-options" element={<FormOptionsPage />} />
          <Route path="currency-settings" element={<CurrencySettingsPage />} />
          <Route path="change-password" element={<ChangePasswordPage />} />
          <Route path="approvals" element={<ApprovalsPage />} />
          <Route path="alerts" element={<AlertsPage />} />
          <Route path="risk-matrix" element={<RiskMatrixPage />} />
        </Route>

        <Route
          element={
            <PermissionRoute
              permissionKey={
                PermissionKeys.resourceManagement.rolesAndPermissions.view
              }
            />
          }
        >
          <Route path="resource/access" element={<ItemLayout />}>
            <Route index element={<AccessManagePage />} />
            <Route path="manage-roles" element={<ItemLayout />}>
              <Route index element={<ManageRolesPage />} />
              <Route path="create" element={<CreateRolePage />} />
              <Route path="edit/:id" element={<EditRolePage />} />
            </Route>
          </Route>
        </Route>

        <Route path="resource/documents" element={<DocumentsPage />} />
      </Route>

      <Route path="" element={<AuthLayout />}>
        <Route path="login" element={<LoginPage />} />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
        <Route path="set-password" element={<SetPasswordPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />
      </Route>
      <Route path="logout" element={<Logout />} loader={LogoutLoader} />
      <Route path="*" element={<RootBoundary />} />
    </Route>,
  ),
);

export default rootRoutes;
